import React from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';

import { Button } from '@appcues/component-library';
import { updateAccountMeta } from 'actions/account/meta';
import { trackEvent } from 'actions/events';
import { showModal } from 'actions/currentModal';
import * as satisfactionSelectors from 'selectors/satisfaction';
import {
  getRecentResponseData,
  getResponsesBreakout,
} from 'helpers/satisfaction';
import * as userSelectors from 'reducers/user';
import * as metaReducer from 'reducers/account/meta';
import QueryResults from 'components/Common/QueryResults';
import Loader from 'components/Common/Loader';
import FeedbackTable from 'components/SatisfactionSurveys/SatisfactionSurveysIndex/Dashboard/SatisfactionFeedback/FeedbackTable';
import { EXPORT_NPS_SURVEY_CSV_MODAL } from 'constants/globals';
import { selectAccountSatisfactionSynced } from 'reducers/account/satisfaction';

/**
 * NPS scores and feedback come as separate events, this munges them
 * together when they are for the same user
 * Also must take into account the ability of a user to update his score
 * In those cases, the last score submitted should prevail.
 * We are asuming results is a sorted array from recent to oldest for this to work
 * (The query that fetches results uses timestamp desc sorting)
 * @param  {Array} results  Result set from analytics api
 * @return {Array}          Results with score & feedback condensed into a
 *                          single entry
 */
export function consolidateResponses(results = []) {
  return Object.values(
    results.reduce((acc, result) => {
      const {
        user_id: userId,
        nps_feedback: npsFeedback,
        nps_raw_score: npsRawScore,
      } = result;

      // filter out events that have neither score nor feedback
      // @todo find out where these are coming from
      if (!npsFeedback && !npsRawScore && npsRawScore !== 0) {
        return acc;
      }

      acc[userId] = acc[userId] || {};
      Object.keys(result).forEach(key => {
        const value = result[key];
        const currentValue = acc[userId][key];
        if ((value || value === 0) && !currentValue && currentValue !== 0) {
          acc[userId][key] = value;
        }
      });

      return acc;
    }, {})
  ).sort((eventA, eventB) => {
    return new Date(eventA.timestamp) > new Date(eventB.timestamp) ? -1 : 1;
  });
}

/**
 * Reformats data to match previous format
 * @param  {Array}  results Result set from analytics api
 * @return {Array}         Response data in old firebase format
 */
function restructureResponseData(results = [], emailProp, userNameProp) {
  return results.map(result => {
    const {
      nps_feedback: feedback,
      nps_raw_score: score,
      timestamp,
      [`a:_identity:${emailProp.toLowerCase()}`]: email,
      [`a:_identity:${userNameProp.toLowerCase()}`]: userName,
    } = result;

    const restructured = {
      attributes: {
        _identity: [],
        feedback,
        score,
      },
      timestamp,
    };
    if (email) {
      // eslint-disable-next-line no-underscore-dangle
      restructured.attributes._identity.push({
        property: emailProp,
        value: email,
      });
    }
    if (userName) {
      // eslint-disable-next-line no-underscore-dangle
      restructured.attributes._identity.push({
        property: userNameProp,
        value: userName,
      });
    }
    return restructured;
  });
}

export const SatisfactionFeedback = ({
  satisfactionSynced,
  responses,
  className,
  emailProp = '',
  userNameProp = '',
  satisfactionId,
  trackEvent: track,
  showModal: show,
  onSave,
}) => {
  if (!satisfactionSynced) {
    return (
      <div className={className}>
        <h2 className="feedback-table-loader-text">
          Retrieving recent survey submissions...
        </h2>
        <Loader />
      </div>
    );
  }

  // responses come from a different source than before; this puts
  // the data back into the legacy format. If we revisit these components,
  // we should get rid of this legacy contract and just use the new data
  const restructuredResponseData = restructureResponseData(
    consolidateResponses(responses),
    emailProp,
    userNameProp
  );

  const transformedResponses = getRecentResponseData(restructuredResponseData);
  const { responsesToday, responsesThisWeek, olderResponses } =
    getResponsesBreakout(transformedResponses);

  const numResponsesToday = responsesToday.length;
  const numResponsesThisWeek = responsesThisWeek.length;
  const numOlderResponses = olderResponses.length;

  const hasResponses = transformedResponses.length > 0;
  const exportButtonLabel = 'Export CSV';

  const showExportModal = () => {
    track('Clicked Button', {
      buttonLabel: exportButtonLabel,
    });

    show(EXPORT_NPS_SURVEY_CSV_MODAL, { stepId: satisfactionId });
  };

  return (
    <div className={className}>
      <div className="feedback-table-controls">
        <h3>
          {hasResponses ? '50 Most Recent Responses' : 'No responses yet'}
        </h3>
        <Button onClick={showExportModal}>{exportButtonLabel}</Button>
      </div>
      <FeedbackTable
        name="Today"
        feedback={responsesToday}
        priority
        emailProp={emailProp}
        userNameProp={userNameProp}
        updateAccountMetaAndFlushMeta={onSave}
        className={numResponsesToday > 0 ? '' : 'empty-table'}
        showSettings={numResponsesToday}
      />
      <hr className={numResponsesThisWeek > 0 ? '' : 'empty-table'} />
      <FeedbackTable
        name="This Week"
        feedback={responsesThisWeek}
        priority
        emailProp={emailProp}
        userNameProp={userNameProp}
        updateAccountMetaAndFlushMeta={onSave}
        className={numResponsesThisWeek > 0 ? '' : 'empty-table'}
        showSettings={!numResponsesToday && numResponsesThisWeek}
      />
      <hr
        className={`table-divider ${
          numOlderResponses > 0 ? '' : 'empty-table'
        }`}
      />
      <FeedbackTable
        name="Older"
        feedback={olderResponses}
        emailProp={emailProp}
        userNameProp={userNameProp}
        updateAccountMetaAndFlushMeta={onSave}
        className={numOlderResponses > 0 ? '' : 'empty-table'}
        showSettings={
          !numResponsesToday && !numResponsesThisWeek && numOlderResponses
        }
      />
    </div>
  );
};

const StyledSatisfactionFeedback = styled(SatisfactionFeedback)`
  width: 100%;
  padding: 20px 0 35px;

  .empty-table {
    display: none;
  }

  .feedback-table-loader-text {
    text-align: center;
    color: #ccc;
  }

  main.loader {
    height: 200px;
  }

  .feedback-table-controls {
    width: 100%;
    margin-bottom: 30px;

    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }

  h3 {
    margin-top: 0 !important;
    font-size: 24px !important;
    color: #4d4d4d;
  }
`;

const SatisfactionFeedbackWithResponses = props => {
  const { emailProp, userNameProp, satisfactionId } = props;
  const query = {
    columns: ['nps_raw_score', 'nps_feedback', 'user_id', 'timestamp'],
    conditions: [
      ['flow_id', '==', satisfactionId],
      ['name', 'in', ['appcues:nps_feedback', 'appcues:nps_score']],
    ],
    start_time: '30 days ago',
    limit: 100,
    order_by: [['timestamp', 'desc']],
    meta: {
      tags: {
        feature: 'NPS',
        page: 'NPS',
        component: 'SatisfactionFeedback',
        description: 'Most recent NPS responses',
      },
    },
  };

  if (emailProp) {
    query.columns.push(`a:_identity:${emailProp}`);
  }

  if (userNameProp) {
    query.columns.push(`a:_identity:${userNameProp}`);
  }

  return (
    <QueryResults query={query}>
      {results => {
        return <StyledSatisfactionFeedback {...props} responses={results} />;
      }}
    </QueryResults>
  );
};

function mapStateToProps(state) {
  if (selectAccountSatisfactionSynced(state)) {
    const accountMeta = metaReducer.selectAccountMeta(state);
    const user = userSelectors.selectUser(state);

    const satisfactionId =
      satisfactionSelectors.selectAccountFirstSatisfactionId(state);

    return {
      satisfactionSynced: selectAccountSatisfactionSynced(state),
      accountId: `${accountMeta.id}`,
      accountUuid: accountMeta.uuid,
      currentUserEmail: user.email,
      satisfactionId,
      emailProp: accountMeta.userEmailField || '',
      userNameProp: accountMeta.userNameField || '',
    };
  }
  return { satisfactionSynced: selectAccountSatisfactionSynced(state) };
}

const mapDispatchToProps = {
  onSave: updateAccountMeta,
  trackEvent,
  showModal,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(SatisfactionFeedbackWithResponses);
